import {Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatButton} from "@angular/material/button";
import {MatRadioModule} from "@angular/material/radio";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatSelectModule} from "@angular/material/select";
import {CommonModule, DatePipe} from "@angular/common";
import {ReportingService} from "../../../services/reporting.service";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {MatTableModule} from "@angular/material/table";
import {MatTabsModule} from "@angular/material/tabs";
import {SnackbarService} from "../../../services/snackbar.service";
import {MatInputModule} from "@angular/material/input";
import {IPayoutReportParams} from "../../../interfaces/reporting/IPayoutReportParams";
import {WinnerPayoutDateFilterTypes} from "../../../interfaces/reporting/WinnerPayoutDateFilterTypes";
import {IPayoutReportResponse} from "../../../interfaces/reporting/IPayoutReportResponse";
import {MatIconModule} from "@angular/material/icon";
import {MatCardModule} from "@angular/material/card";
import {PipesModule} from "../../../pipes/pipes.module";
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import {Content, TDocumentDefinitions} from "pdfmake/interfaces";
import {PdfUtilityService} from "../../../services/pdf-utility.service";
import {FormatCurrencyPipe} from "../../../pipes/format-currency.pipe";
import {WinnerPayoutStatusPipe} from "../../../pipes/winner-payout-status.pipe";
import {UtilService} from "../../../services/util.service";

(<any>pdfMake).vfs = pdfFonts.pdfMake.vfs;

@Component({
  selector: 'app-payout-report',
  standalone: true,
  templateUrl: './payout-report.component.html',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatButton,
    MatRadioModule,
    MatCheckboxModule,
    MatSelectModule,
    CommonModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTabsModule,
    MatInputModule,
    MatIconModule,
    MatCardModule,
    DatePipe,
    PipesModule
  ],
  styleUrls: ['./payout-report.component.scss']
})
export class PayoutReportComponent implements OnInit {
  @Input() set gameSetter(gameP: IGameQueryResult) {
    this.activeGame = gameP;
    this.payoutForm.patchValue({gameId: gameP.Id});
  }

  @ViewChild('resultsSection', {read: ElementRef}) resultsSection?: ElementRef;

  public activeGame: IGameQueryResult | null = null;
  public loading = false;
  public reportData: IPayoutReportResponse | null = null;
  public showReports = false;
  public filtersCollapsed = false;

  // Summary statistics - calculated once when report loads
  public charityPayoutCount = 0;
  public charityPayoutTotal = 0;
  public winnerPayoutCount = 0;
  public winnerPayoutTotal = 0;
  public grandTotalPayouts = 0;
  public grandTotalCount = 0;
  public filterSummary = '';

  public payoutForm = new FormGroup({
    gameId: new FormControl<string>('', Validators.required),
    includeWinnerPayouts: new FormControl<boolean>(true),
    includeCharityPayouts: new FormControl<boolean>(true),
    dateRangeType: new FormControl<'range' | 'single'>('range', Validators.required),
    startDate: new FormControl<string>('', Validators.required),
    endDate: new FormControl<string>(''),
    charityPayoutVoidedFilter: new FormControl<string>('not_voided', Validators.required),
    winnerPayoutStatusesNotPaid: new FormControl<boolean>(true),
    winnerPayoutStatusesPaid: new FormControl<boolean>(true),
    winnerPayoutStatusesForfeited: new FormControl<boolean>(false),
    winnerPayoutStatusesExpired: new FormControl<boolean>(false),
    dateFilterType: new FormControl<WinnerPayoutDateFilterTypes>(WinnerPayoutDateFilterTypes.DrawDate, Validators.required)
  });

  // Table columns
  public charityPayoutColumns: string[] = ['charityName', 'payoutAmount', 'createdOnDate', 'voided'];
  public winnerPayoutColumns: string[] = ['playerName', 'playerEmail', 'ticketNumber', 'payoutAmount', 'status', 'paidOutOnDate'];

  constructor(
    private reportingService: ReportingService,
    private snackbarService: SnackbarService,
    private pdfUtilityService: PdfUtilityService,
    private currencyPipe: FormatCurrencyPipe,
    private winnerPayoutStatusPipe: WinnerPayoutStatusPipe,
    private datePipe: DatePipe,
    private utilService: UtilService
  ) {
  }

  ngOnInit() {
    // Watch for date range type changes to manage end date validation
    this.payoutForm.get('dateRangeType')?.valueChanges.subscribe(value => {
      const endDateControl = this.payoutForm.get('endDate');
      if (value === 'range') {
        endDateControl?.setValidators([Validators.required]);
      } else {
        endDateControl?.clearValidators();
        endDateControl?.setValue('');
      }
      endDateControl?.updateValueAndValidity();
    });
  }

  public onSubmit() {
    // Validate form
    if (this.payoutForm.invalid) {
      this.payoutForm.markAllAsTouched();
      return;
    }

    // Check that at least one payout type is selected
    const includeWinner = this.payoutForm.value.includeWinnerPayouts;
    const includeCharity = this.payoutForm.value.includeCharityPayouts;

    if (!includeWinner && !includeCharity) {
      alert('Please select at least one payout type (Winner or Charity)');
      return;
    }

    // Build winner payout status filter
    const statuses: number[] = [];
    if (this.payoutForm.value.winnerPayoutStatusesNotPaid) statuses.push(1);
    if (this.payoutForm.value.winnerPayoutStatusesPaid) statuses.push(2);
    if (this.payoutForm.value.winnerPayoutStatusesForfeited) statuses.push(3);
    if (this.payoutForm.value.winnerPayoutStatusesExpired) statuses.push(4);

    const params: IPayoutReportParams = {
      gameId: this.payoutForm.value.gameId!,
      includeWinnerPayouts: includeWinner!,
      includeCharityPayouts: includeCharity!,
      useDateRange: this.payoutForm.value.dateRangeType === 'range',
      startDate: this.payoutForm.value.startDate!,
      charityPayoutVoidedFilter: this.payoutForm.value.charityPayoutVoidedFilter!,
      winnerPayoutStatusFilter: statuses.length > 0 ? statuses.join(',') : 'all',
      dateFilterType: this.payoutForm.value.dateFilterType!
    };

    if (params.useDateRange) {
      params.endDate = this.payoutForm.value.endDate!;
    }

    this.loading = true;
    this.showReports = false;
    this.reportingService.getPayoutReport(params).subscribe({
      next: (response) => {
        this.reportData = response;

        // Calculate summary statistics once
        this.calculateSummaryStatistics();

        // Generate filter summary
        this.filterSummary = this.buildFilterSummary();

        this.showReports = true;
        this.loading = false;

        // Collapse filters and scroll to results for better UX
        this.filtersCollapsed = true;
        setTimeout(() => this.scrollToResults(), 100);
      },
      error: (error) => {
        console.error('Error fetching payout report:', error);
        this.snackbarService.openErrorSnackBar('Failed to fetch payout report. Please try again.');
        this.loading = false;
      }
    });
  }

  public toggleFilters(): void {
    this.filtersCollapsed = !this.filtersCollapsed;
  }

  private buildFilterSummary(): string {
    const parts: string[] = [];

    // Date range
    const startDate = this.payoutForm.value.startDate;
    const endDate = this.payoutForm.value.endDate;
    const isRange = this.payoutForm.value.dateRangeType === 'range';

    if (startDate && endDate && isRange) {
      parts.push(`${this.formatDateValue(startDate)} - ${this.formatDateValue(endDate)}`);
    } else if (startDate) {
      parts.push(this.formatDateValue(startDate));
    }

    // Payout types
    const types: string[] = [];
    if (this.payoutForm.value.includeWinnerPayouts) types.push('Winner');
    if (this.payoutForm.value.includeCharityPayouts) types.push('Charity');
    if (types.length > 0) {
      parts.push(types.join(' & '));
    }

    // Date filter type (only relevant for winner payouts)
    if (this.payoutForm.value.includeWinnerPayouts) {
      const dateFilterType = this.payoutForm.value.dateFilterType === WinnerPayoutDateFilterTypes.DrawDate ? 'Draw Date' : 'Payout Date';
      parts.push(`Filter by: ${dateFilterType}`);
    }

    return parts.join(' | ');
  }

  private calculateSummaryStatistics(): void {
    // Charity payouts - only non-voided
    const nonVoidedCharityPayouts = this.reportData?.CharityPayouts?.filter(p => !p.Voided) || [];
    this.charityPayoutCount = nonVoidedCharityPayouts.length;
    this.charityPayoutTotal = nonVoidedCharityPayouts.reduce((sum, payout) => sum + payout.PayoutAmount, 0);

    // Winner payouts - only paid (status = 2)
    const paidWinnerPayouts = this.reportData?.WinnerPayouts?.filter(p => p.PayoutStatus === 2) || [];
    this.winnerPayoutCount = paidWinnerPayouts.length;
    this.winnerPayoutTotal = paidWinnerPayouts.reduce((sum, payout) => sum + payout.PayoutAmount, 0);

    // Grand totals
    this.grandTotalPayouts = this.charityPayoutTotal + this.winnerPayoutTotal;
    this.grandTotalCount = this.charityPayoutCount + this.winnerPayoutCount;
  }

  private scrollToResults(): void {
    if (this.resultsSection?.nativeElement) {
      this.resultsSection.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  // Utility method for date formatting (only used in buildFilterSummary)
  private formatDateValue(date: Date | string): string {
    return new Date(date).toLocaleDateString();
  }

  public generatePDF(): void {
    if (!this.reportData) return;

    const contentP: Content[] = [];

    // Title
    const dateRangeText: string | null = this.payoutForm.value.dateRangeType === 'range'
      ? `${this.datePipe.transform(this.payoutForm.value.startDate, 'yyyy-MM-dd')} to ${this.datePipe.transform(this.payoutForm.value.endDate, 'yyyy-MM-dd')}`
      : this.datePipe.transform(this.payoutForm.value.startDate, 'yyyy-MM-dd');

    contentP.push({
      text: `Payout Report for ${this.activeGame?.Name || 'Game'}`,
      style: this.pdfUtilityService.PdfStyles.pageTitle,
      margin: [0, 0, 0, 4]
    });

    contentP.push({
      text: dateRangeText ?? 'Cannot Format Date',
      style: this.pdfUtilityService.PdfStyles.subtitle,
      margin: [0, 0, 0, 8]
    });

    // Filter Summary Section
    contentP.push(this.getFilterDetails());
    contentP.push(this.pdfUtilityService.AddLineBreak());

    // Summary Statistics Section
    contentP.push(this.getSummaryStatistics());
    contentP.push(this.pdfUtilityService.AddLineBreak());

    // Charity Payouts Table
    if (this.payoutForm.value.includeCharityPayouts && this.reportData.CharityPayouts.length > 0) {
      contentP.push(this.getCharityPayoutsTable());
      contentP.push(this.pdfUtilityService.AddLineBreak());
    }

    // Winner Payouts Table
    if (this.payoutForm.value.includeWinnerPayouts && this.reportData.WinnerPayouts.length > 0) {
      contentP.push(this.getWinnerPayoutsTable());
    }

    const docDef: TDocumentDefinitions = {
      pageMargins: [40, 40, 40, 40],
      footer: (currentPage, pageCount) => {
        return {
          text: 'Page ' + currentPage.toString() + ' of ' + pageCount,
          style: this.pdfUtilityService.PdfStyles.footer
        };
      },
      content: contentP
    };

    const pdf = pdfMake.createPdf(docDef);
    pdf.open();
  }

  private getFilterDetails(): Content[] {
    const filterDetails: Content[] = [];
    filterDetails.push(this.pdfUtilityService.generateSectionTitle('Report Filters'));

    // Date range
    const dateRangeType = this.payoutForm.value.dateRangeType === 'range' ? 'Date Range' : 'Single Date';
    filterDetails.push(this.pdfUtilityService.generateLabelValueRow('Date Range Type: ', dateRangeType));

    if (this.payoutForm.value.dateRangeType === 'range') {
      filterDetails.push(this.pdfUtilityService.generateLabelValueRow('Start Date: ', this.datePipe.transform(this.payoutForm.value.startDate, 'yyyy-MM-dd') || ''));
      filterDetails.push(this.pdfUtilityService.generateLabelValueRow('End Date: ', this.datePipe.transform(this.payoutForm.value.endDate, 'yyyy-MM-dd') || ''));
    } else {
      filterDetails.push(this.pdfUtilityService.generateLabelValueRow('Date: ', this.datePipe.transform(this.payoutForm.value.startDate, 'yyyy-MM-dd') || ''));
    }

    // Payout types
    const payoutTypes: string[] = [];
    if (this.payoutForm.value.includeWinnerPayouts) payoutTypes.push('Winner Payouts');
    if (this.payoutForm.value.includeCharityPayouts) payoutTypes.push('Charity Payouts');
    filterDetails.push(this.pdfUtilityService.generateLabelValueRow('Payout Types: ', payoutTypes.join(', ')));

    // Charity voided filter
    const voidedFilterMap: Record<string, string> = {
      'not_voided': 'Not Voided Only',
      'voided': 'Voided Only',
      'all': 'All'
    };
    const voidedFilterText = voidedFilterMap[this.payoutForm.value.charityPayoutVoidedFilter || 'not_voided'];
    filterDetails.push(this.pdfUtilityService.generateLabelValueRow('Charity Voided Filter: ', voidedFilterText));

    // Winner status filters
    const winnerStatuses: string[] = [];
    if (this.payoutForm.value.winnerPayoutStatusesNotPaid) winnerStatuses.push('Not Paid');
    if (this.payoutForm.value.winnerPayoutStatusesPaid) winnerStatuses.push('Paid');
    if (this.payoutForm.value.winnerPayoutStatusesForfeited) winnerStatuses.push('Forfeited');
    if (this.payoutForm.value.winnerPayoutStatusesExpired) winnerStatuses.push('Expired');
    filterDetails.push(this.pdfUtilityService.generateLabelValueRow('Winner Status Filter: ', winnerStatuses.length > 0 ? winnerStatuses.join(', ') : 'None'));

    // Date filter type (for winner payouts)
    if (this.payoutForm.value.includeWinnerPayouts) {
      const dateFilterTypeText = this.payoutForm.value.dateFilterType === WinnerPayoutDateFilterTypes.DrawDate ? 'Draw Date' : 'Payout Date';
      filterDetails.push(this.pdfUtilityService.generateLabelValueRow('Winner Date Filter Type: ', dateFilterTypeText));
    }

    return filterDetails;
  }

  private getSummaryStatistics(): Content[] {
    const summaryStats: Content[] = [];
    summaryStats.push(this.pdfUtilityService.generateSectionTitle('Summary Statistics'));

    // Charity Payout Summary
    if (this.payoutForm.value.includeCharityPayouts) {
      summaryStats.push({
        text: 'Charity Payouts',
        fontSize: 11,
        bold: true,
        margin: [16, 8, 0, 4]
      });
      summaryStats.push(this.pdfUtilityService.generateLabelValueRowWithLargeLeft('Total Amount: ', this.currencyPipe.transform(this.charityPayoutTotal) || '$0.00'));
      summaryStats.push(this.pdfUtilityService.generateLabelValueRowWithLargeLeft('Number of Payouts: ', this.charityPayoutCount.toString()));
    }

    // Winner Payout Summary
    if (this.payoutForm.value.includeWinnerPayouts) {
      summaryStats.push({
        text: 'Winner Payouts',
        fontSize: 11,
        bold: true,
        margin: [16, 8, 0, 4]
      });
      summaryStats.push(this.pdfUtilityService.generateLabelValueRowWithLargeLeft('Total Amount: ', this.currencyPipe.transform(this.winnerPayoutTotal) || '$0.00'));
      summaryStats.push(this.pdfUtilityService.generateLabelValueRowWithLargeLeft('Number of Payouts: ', this.winnerPayoutCount.toString()));
    }

    // Grand Total
    summaryStats.push({
      text: 'Grand Total',
      fontSize: 11,
      bold: true,
      margin: [16, 8, 0, 4]
    });
    summaryStats.push(this.pdfUtilityService.generateLabelValueRowWithLargeLeft('Total Amount: ', this.currencyPipe.transform(this.grandTotalPayouts) || '$0.00'));
    summaryStats.push(this.pdfUtilityService.generateLabelValueRowWithLargeLeft('Total Payouts: ', this.grandTotalCount.toString()));

    return summaryStats;
  }

  private getCharityPayoutsTable(): Content[] {
    const charityTable: Content[] = [];
    charityTable.push(this.pdfUtilityService.generateSectionTitle('Charity Payouts'));

    const tableContent: Content = {
      layout: 'lightHorizontalLines',
      table: {
        headerRows: 1,
        widths: ['*', 'auto', 'auto', 'auto'],
        body: [
          [
            { text: 'Charity Name', fontSize: 10, bold: true },
            { text: 'Amount', fontSize: 10, bold: true },
            { text: 'Date', fontSize: 10, bold: true },
            { text: 'Voided', fontSize: 10, bold: true }
          ]
        ]
      },
      margin: [0, 8, 0, 16]
    };

    this.reportData?.CharityPayouts.forEach((payout) => {
      tableContent.table.body.push([
        { text: payout.CharityName, fontSize: 9 },
        { text: this.currencyPipe.transform(payout.PayoutAmount) || '$0.00', fontSize: 9 },
        { text: this.datePipe.transform(payout.CreatedOnDate, 'yyyy-MM-dd') || '', fontSize: 9 },
        { text: payout.Voided ? 'Yes' : 'No', fontSize: 9 }
      ]);
    });

    charityTable.push(tableContent);
    return charityTable;
  }

  private getWinnerPayoutsTable(): Content[] {
    const winnerTable: Content[] = [];
    winnerTable.push(this.pdfUtilityService.generateSectionTitle('Winner Payouts'));

    const tableContent: Content = {
      layout: 'lightHorizontalLines',
      table: {
        headerRows: 1,
        widths: ['*', '*', 'auto', 'auto', 'auto', 'auto'],
        body: [
          [
            { text: 'Player Name', fontSize: 10, bold: true },
            { text: 'Email', fontSize: 10, bold: true },
            { text: 'Ticket #', fontSize: 10, bold: true },
            { text: 'Amount', fontSize: 10, bold: true },
            { text: 'Status', fontSize: 10, bold: true },
            { text: 'Paid Date', fontSize: 10, bold: true }
          ]
        ]
      },
      margin: [0, 8, 0, 16]
    };

    this.reportData?.WinnerPayouts.forEach((payout) => {
      tableContent.table.body.push([
        { text: `${payout.PlayerFirstName} ${payout.PlayerLastName}`, fontSize: 9 },
        { text: payout.PlayerEmail, fontSize: 9 },
        { text: payout.TicketNumber.toString(), fontSize: 9 },
        { text: this.currencyPipe.transform(payout.PayoutAmount) || '$0.00', fontSize: 9 },
        { text: this.winnerPayoutStatusPipe.transform(payout.PayoutStatus), fontSize: 9 },
        { text: payout.PaidOutOnDate ? this.datePipe.transform(payout.PaidOutOnDate, 'yyyy-MM-dd') || '' : '', fontSize: 9 }
      ]);
    });

    winnerTable.push(tableContent);
    return winnerTable;
  }

  public downloadCharityPayoutsCSV(): void {
    if (!this.reportData || !this.reportData.CharityPayouts || this.reportData.CharityPayouts.length === 0) {
      this.snackbarService.openErrorSnackBar('No charity payout data to download');
      return;
    }

    const formatted_date = this.datePipe.transform(new Date(), 'yyyy-MM-dd')!;
    const filename = `charity-payouts-${formatted_date}.csv`;

    // Convert charity payouts to CSV-friendly format
    const csvData = this.reportData.CharityPayouts.map(payout => ({
      'Charity Name': payout.CharityName,
      'Payout Amount': this.currencyPipe.transform(payout.PayoutAmount) || '$0.00',
      'Created On': this.datePipe.transform(payout.CreatedOnDate, 'yyyy-MM-dd') || '',
      'Voided': payout.Voided ? 'Yes' : 'No'
    }));

    console.log(this.reportData.CharityPayouts);
    console.log(csvData);
    this.utilService.generateTableCSV(csvData, filename);
  }

  public downloadWinnerPayoutsCSV(): void {
    if (!this.reportData || !this.reportData.WinnerPayouts || this.reportData.WinnerPayouts.length === 0) {
      this.snackbarService.openErrorSnackBar('No winner payout data to download');
      return;
    }

    const formatted_date = this.datePipe.transform(new Date(), 'yyyy-MM-dd')!;
    const filename = `winner-payouts-${formatted_date}.csv`;

    // Convert winner payouts to CSV-friendly format
    const csvData = this.reportData.WinnerPayouts.map(payout => ({
      'Player Name': `${payout.PlayerFirstName} ${payout.PlayerLastName}`,
      'Email': payout.PlayerEmail,
      'Ticket Number': payout.TicketNumber.toString(),
      'Payout Amount': this.currencyPipe.transform(payout.PayoutAmount) || '$0.00',
      'Status': this.winnerPayoutStatusPipe.transform(payout.PayoutStatus),
      'Paid Out Date': payout.PaidOutOnDate ? this.datePipe.transform(payout.PaidOutOnDate, 'yyyy-MM-dd') || '' : 'N/A'
    }));

    this.utilService.generateTableCSV(csvData, filename);
  }

  public downloadAllCSV(): void {
    if (!this.reportData) {
      this.snackbarService.openErrorSnackBar('No report data available to download');
      return;
    }

    let filesGenerated = 0;

    // Download charity payouts if included and has data
    if (this.payoutForm.value.includeCharityPayouts && this.reportData.CharityPayouts && this.reportData.CharityPayouts.length > 0) {
      this.downloadCharityPayoutsCSV();
      filesGenerated++;
    }

    // Download winner payouts if included and has data
    if (this.payoutForm.value.includeWinnerPayouts && this.reportData.WinnerPayouts && this.reportData.WinnerPayouts.length > 0) {
      this.downloadWinnerPayoutsCSV();
      filesGenerated++;
    }

    if (filesGenerated === 0) {
      this.snackbarService.openErrorSnackBar('No payout data available to download');
    } else {
      this.snackbarService.openSuccessfulSnackBar(`${filesGenerated} CSV file(s) downloaded successfully`);
    }
  }
}
